import {
  AuditUseCase,
  AuditTask,
  AuditResult,
  TASK_STATUS,
  RESULT_STATUS,
  UseCaseConfig,
  USE_CASE_CATEGORY,
  DATA_SOURCE_TYPE,
  COMPARISON_MODE,
  FIELD_TYPE,
  makeReviewSampling,
} from "./types";

// Placeholder alias used in sample data. Thunks replace this with the
// logged-in user so isMine() matches, while preserving other reviewer IDs.
export const SAMPLE_AUDITOR_ALIAS = "sample-auditor";

// TODO: Remove all sample data once audit v2 migration is done
export const SAMPLE_USE_CASES: AuditUseCase[] = [
  { id: "uc-safety-genie", name: "Safety Genie – Q&A Accuracy", inter_rater_count: 1 },
  { id: "uc-safety-vision", name: "Safety Vision – PPE Detection", inter_rater_count: 2 },
  { id: "uc-custom-ergo", name: "Custom – Ergo Risk Scoring", inter_rater_count: 1 },
];

export const SAMPLE_TASKS: AuditTask[] = [
  {
    id: "task-0001-abcd-1234-efgh",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 2,
    created_at: "2026-01-10T08:00:00Z",
  },
  {
    id: "task-0002-abcd-1234-efgh",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: "sample-auditor",
    priority: 1,
    created_at: "2026-01-11T10:30:00Z",
  },
  {
    id: "task-0003-abcd-1234-efgh",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.COMPLETED,
    assigned_to: "sample-auditor",
    priority: 0,
    created_at: "2026-01-08T14:00:00Z",
  },
  {
    id: "task-0004-abcd-1234-efgh",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.COMPLETED,
    assigned_to: "sample-auditor",
    priority: 0,
    created_at: "2026-01-07T09:00:00Z",
  },
  {
    id: "task-0005-abcd-1234-efgh",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.EXPIRED,
    assigned_to: "sample-auditor",
    priority: 0,
    created_at: "2026-01-01T12:00:00Z",
  },
  {
    id: "task-0006-abcd-1234-efgh",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: "sample-auditor",
    priority: 3,
    created_at: "2026-01-12T07:00:00Z",
  },
];

export const SAMPLE_RESULTS: AuditResult[] = [
  {
    id: "res-001",
    task_id: "task-0003-abcd-1234-efgh",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { accuracy: { decision: "correct" }, completeness: { decision: "correct" } },
  },
  {
    id: "res-002",
    task_id: "task-0004-abcd-1234-efgh",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { bbox_quality: { decision: "correct" }, label_match: { decision: "incorrect" } },
  },
  {
    id: "res-003",
    task_id: "task-0004-abcd-1234-efgh",
    auditor_id: "reviewer02",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { bbox_quality: { decision: "correct" }, label_match: { decision: "correct" } },
  },
  {
    id: "res-004",
    task_id: "task-0002-abcd-1234-efgh",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.DRAFT,
    decisions: {},
  },
];

// ----------------------------  Sample Config Data  ----------------------------

export const SAMPLE_USE_CASE_CONFIGS: UseCaseConfig[] = [
  {
    id: "uc-safety-genie",
    name: "Safety Genie - Q&A Accuracy",
    description: "Evaluate Safety Genie chatbot answers for accuracy and completeness.",
    category: USE_CASE_CATEGORY.SAFETY_GENIE,
    data_source_type: DATA_SOURCE_TYPE.TEXT_ONLY,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 60,
    status: "active",
    fields: [
      {
        id: "f-sg-question",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "Question",
        options: [],
        mandatory: true,
        help_text: "The original user question sent to Safety Genie.",
        display_order: 0,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sg-answer",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "AI Answer",
        options: [],
        mandatory: true,
        help_text: "The answer generated by Safety Genie.",
        display_order: 1,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sg-accuracy",
        type: FIELD_TYPE.DROPDOWN_SINGLE,
        label: "Accuracy",
        options: ["Correct", "Partially Correct", "Incorrect"],
        mandatory: true,
        help_text: "Rate the factual accuracy of the answer.",
        display_order: 2,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
    ],
    checklist_items: [
      { id: "cl-sg-1", label: "Answer addresses the question", display_order: 0 },
      { id: "cl-sg-2", label: "No hallucinated facts", display_order: 1 },
      { id: "cl-sg-3", label: "Proper citation of sources", display_order: 2 },
    ],
    created_at: "2026-01-05T10:00:00Z",
    updated_at: "2026-01-05T10:00:00Z",
  },
  {
    id: "uc-safety-vision",
    name: "Safety Vision - PPE Detection",
    description: "Validate PPE detection model outputs on warehouse images.",
    category: USE_CASE_CATEGORY.SAFETY_VISION,
    data_source_type: DATA_SOURCE_TYPE.SINGLE_IMAGE_TEXT,
    comparison_mode: COMPARISON_MODE.OVERRIDE,
    inter_rater_count: 2,
    review_sampling: makeReviewSampling({
      second_pct: 10,
      second_enabled: true,
      second_reviewer: "auditor02",
    }),
    claim_expiry_minutes: 120,
    status: "active",
    fields: [
      {
        id: "f-sv-image",
        type: FIELD_TYPE.IMAGE_VIEWER,
        label: "Source Image",
        options: [],
        mandatory: true,
        help_text: "The warehouse camera image.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sv-ppe-type",
        type: FIELD_TYPE.DROPDOWN_MULTI,
        label: "PPE Detected",
        options: ["Hard Hat", "Safety Vest", "Gloves", "Safety Glasses"],
        mandatory: true,
        help_text: "Select all PPE types detected in the image.",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: true,
      },
    ],
    checklist_items: [
      { id: "cl-sv-1", label: "Bounding boxes are accurate", display_order: 0 },
      { id: "cl-sv-2", label: "All PPE items identified", display_order: 1 },
    ],
    created_at: "2026-01-06T08:00:00Z",
    updated_at: "2026-01-06T08:00:00Z",
  },
  {
    id: "uc-custom-ergo",
    name: "Custom - Ergo Risk Scoring",
    description: "Review ergonomic risk scoring results.",
    category: USE_CASE_CATEGORY.CUSTOM,
    data_source_type: DATA_SOURCE_TYPE.VIDEO_TEXT,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 240,
    status: "inactive",
    fields: [
      {
        id: "f-ergo-video",
        type: FIELD_TYPE.VIDEO_PLAYER,
        label: "Task Video",
        options: [],
        mandatory: true,
        help_text: "Video of the associate performing the task.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-ergo-score",
        type: FIELD_TYPE.NUMERIC,
        label: "Risk Score",
        options: [],
        mandatory: true,
        help_text: "Numeric risk score (1-10).",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: 1,
        max: 10,
        step: 1,
        allow_other: false,
      },
    ],
    checklist_items: [{ id: "cl-ergo-1", label: "Posture evaluated correctly", display_order: 0 }],
    created_at: "2026-01-07T12:00:00Z",
    updated_at: "2026-01-07T12:00:00Z",
  },
];
